import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

export async function generateEditableTDCReport(
  analysisData,
  alertBytes,
  warningBytes,
  clockBytes,
  calendarBytes,
  pdfIconBytes,
  logoBytes,
  fastTrack,
  phase1BudgetPeriod
) {
  const pdfDoc = await PDFDocument.create();
  const form = pdfDoc.getForm();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const requiredFixesCount =
    analysisData.required_fixes_count ??
    (analysisData.required_fixes?.length || 0);
  const pmDiscretionCount =
    analysisData.pm_discretion_count ??
    (analysisData.pm_discretion?.length || 0);

  const iconRed = await pdfDoc.embedPng(alertBytes);
  const iconYellow = await pdfDoc.embedPng(warningBytes);
  const iconClock = await pdfDoc.embedPng(clockBytes);
  const iconCalendar = await pdfDoc.embedPng(calendarBytes);
  const pdfIconImg = await pdfDoc.embedPng(pdfIconBytes);
  const logoImg = await pdfDoc.embedPng(logoBytes);

  function sanitizePdfText(input) {
    if (!input) return '';
    return input.replace(/[\u25E6\u2022\u2023\u2043\u2219\u25AA\u25AB]/g, '-');
  }

  function splitTextIntoLines(text, maxWidth, fontObj, fontSize) {
    text = sanitizePdfText(text);
    if (!text) return [''];
    const lines = [];
    for (const block of text.split('\n')) {
      if (block.trim() === '') continue;
      let line = '';
      for (const word of block.split(' ')) {
        if (fontObj.widthOfTextAtSize(word, fontSize) > maxWidth) {
          if (line) {
            lines.push(line);
            line = '';
          }
          let subWord = '';
          for (let c = 0; c < word.length; c++) {
            const testSub = subWord + word[c];
            if (
              fontObj.widthOfTextAtSize(testSub, fontSize) > maxWidth &&
              subWord
            ) {
              lines.push(subWord);
              subWord = word[c];
            } else {
              subWord = testSub;
            }
          }
          if (subWord) lines.push(subWord);
          continue;
        }
        const testLine = line ? line + ' ' + word : word;
        if (
          fontObj.widthOfTextAtSize(testLine, fontSize) > maxWidth &&
          line
        ) {
          lines.push(line);
          line = word;
        } else {
          line = testLine;
        }
      }
      if (line && line !== '\n' && line !== '\r') lines.push(line);
    }
    return lines.filter((l) => l && l !== '\n' && l !== '\r');
  }

  function drawBadgeWithIcon(page, x, y, iconImg, count) {
    const badgeWidth = 100;
    const badgeHeight = 20;
    const radius = 10;
    page.drawRectangle({
      x,
      y: y - 2,
      width: badgeWidth,
      height: badgeHeight,
      borderColor: rgb(0.86, 0.86, 0.86),
      borderWidth: 1.2,
      borderRadius: radius,
      opacity: 1,
    });
    page.drawImage(iconImg, {
      x: x + 7,
      y: y + 2,
      width: 14,
      height: 14,
    });
    page.drawText(`${count} ISSUES`, {
      x: x + 24,
      y: y + 6,
      size: 10,
      font: boldFont,
      color: rgb(0.07, 0.07, 0.07),
    });
  }

  let page = pdfDoc.addPage([612, 792]);
  const topMargin = 24;
  let y = 760 - topMargin;
  const left = 50;
  const width = 512;

  // HEADER
  const logoDims = logoImg.scale(1);
  const desiredHeight = 32;
  const scale = desiredHeight / logoDims.height;
  const logoWidth = logoDims.width * scale;
  const logoHeight = logoDims.height * scale;
  let headerSpacing = 24;

  page.drawImage(logoImg, {
    x: left,
    y: y - 2,
    width: logoWidth,
    height: logoHeight,
  });
  y -= logoHeight + 8;

  page.drawText('LLM Analysis', {
    x: left,
    y,
    size: 12,
    font,
    color: rgb(43 / 255, 42 / 255, 42 / 255),
  });
  y -= headerSpacing;

  page.drawText(
    analysisData.project_name ||
      analysisData.projectname ||
      analysisData.filename ||
      '',
    { x: left, y, size: 17, font: boldFont, color: rgb(0.12, 0.12, 0.12) }
  );
  y -= headerSpacing;

  page.drawImage(iconCalendar, { x: left, y: y - 1, width: 12, height: 12 });
  page.drawText(' ' + (analysisData.date?.split('\n')[0] || ''), {
    x: left + 16,
    y,
    size: 12,
    font,
    color: rgb(0.3, 0.3, 0.3),
  });
  page.drawImage(iconClock, {
    x: left + 140,
    y: y - 1,
    width: 12,
    height: 12,
  });
  page.drawText(' ' + (analysisData.date?.split('\n')[1] || ''), {
    x: left + 156,
    y,
    size: 12,
    font,
    color: rgb(0.3, 0.3, 0.3),
  });
  y -= headerSpacing;

  page.drawLine({
    start: { x: left, y },
    end: { x: left + width, y },
    thickness: 1,
    color: rgb(0.85, 0.85, 0.85),
  });
  y -= 22;

  // FILES
  page.drawText('Files:', { x: left, y, size: 12, font: boldFont });
  y -= 22;

  const fileIconWidth = 18;
  const fileIconHeight = 18;
  const fileIconGap = 8;
  for (const f of analysisData.files || []) {
    page.drawImage(pdfIconImg, {
      x: left + 2,
      y: y - 2,
      width: fileIconWidth,
      height: fileIconHeight,
    });
    page.drawText(f, {
      x: left + 32,
      y,
      size: 12,
      font,
      color: rgb(0.2, 0.2, 0.2),
    });
    y -= fileIconHeight + fileIconGap;
  }

  y -= 30;

  const isFastTrack = String(fastTrack).trim().toLowerCase() === 'yes';
  page.drawText('Input Application type:', {
    x: left,
    y,
    size: 12,
    font: boldFont,
  });
  page.drawText(` ${isFastTrack ? 'Fast Track' : 'Not Fast Track'}`, {
    x: left + 132,
    y,
    size: 12,
    font,
  });
  y -= 20;

  if (isFastTrack) {
    page.drawText('Phase 1 Budget Period(s):', {
      x: left,
      y,
      size: 12,
      font: boldFont,
    });
    page.drawText(
      ` ${phase1BudgetPeriod.length ? phase1BudgetPeriod.join(', ') : 'None'}`,
      { x: left + 150, y, size: 12, font }
    );
    y -= 20;
  }

  y -= 8;
  page.drawLine({
    start: { x: left, y },
    end: { x: left + width, y },
    thickness: 1,
    color: rgb(0.85, 0.85, 0.85),
  });
  y -= 24;

  // SUMMARY
  page.drawText('Summary', {
    x: left,
    y,
    size: 12,
    font: boldFont,
    color: rgb(0, 0, 0),
  });
  y -= 32;

  page.drawImage(iconRed, { x: left, y: y - 2, width: 12, height: 13 });
  page.drawText('Required Fixes:', {
    x: left + 20,
    y,
    size: 12,
    font: boldFont,
    color: rgb(0.8, 0.1, 0.1),
  });
  page.drawText(
    ` ${
      analysisData.required_fixes_count ??
      (analysisData.required_fixes?.length || 0)
    }`,
    { x: left + 110, y, size: 12, font: boldFont, color: rgb(0.8, 0.1, 0.1) }
  );
  y -= 26;

  page.drawImage(iconYellow, { x: left, y: y - 2, width: 12, height: 13 });
  page.drawText('PM Discretion:', {
    x: left + 20,
    y,
    size: 12,
    font: boldFont,
    color: rgb(0.95, 0.75, 0.18),
  });
  page.drawText(
    ` ${
      analysisData.pm_discretion_count ??
      (analysisData.pm_discretion?.length || 0)
    }`,
    { x: left + 105, y, size: 12, font: boldFont, color: rgb(0.95, 0.75, 0.18) }
  );
  y -= 32;

  page.drawLine({
    start: { x: left, y },
    end: { x: left + width, y },
    thickness: 1,
    color: rgb(0.85, 0.85, 0.85),
  });
  y -= 30;

  const boxLeft = left + 20;
  const boxWidth = width;

  function renderCardNoRepeatCategory({
    icon,
    id,
    category,
    llm_analysis,
    badgeName,
    reasonColor,
    page,
    y,
  }) {
    const fontSize = 12;
    const boxPaddingTop = 20;
    const reasonGap = 22;
    const textFieldHeight = 10 * 1.3 * 3 + 12;
    const extraPadding = 36;
    const categoryGap = 14;

    const catLines = splitTextIntoLines(
      category || '',
      width - 80,
      boldFont,
      13
    );
    const categoryHeight = catLines.length * 18;

    // ===== build a single readable analysis block =====
let analysisBlock = '';

if (llm_analysis) {
  const rawLines = llm_analysis
    .split('\n')
    .map((l) => sanitizePdfText(l).trim())
    .filter(Boolean);

  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i];

    const isNameLine =
      /^-\s*[A-Za-z]/.test(line) && !/^-\s*Page\s*\d+/i.test(line);

    const next = rawLines[i + 1] || '';
    const nextIsName =
      /^-\s*[A-Za-z]/.test(next) && !/^-\s*Page\s*\d+/i.test(next);

    analysisBlock += line + '\n';

    // blank line between different people
    if (!isNameLine && nextIsName) {
      analysisBlock += '\n\n';
    }
  }
}

if (!analysisBlock) {
  analysisBlock = '';
}

// wrap whole block to inner box width
const innerWidth = boxWidth - 30;
const analysisLines = splitTextIntoLines(
  analysisBlock,
  innerWidth,
  font,
  fontSize
);

const analysisHeight = analysisLines.length * fontSize * 1.35;

const boxHeight =
  boxPaddingTop + analysisHeight + reasonGap + textFieldHeight + extraPadding;

const totalHeight = categoryHeight + categoryGap + boxHeight;

if (y - totalHeight < 80) {
  page = pdfDoc.addPage([612, 792]);
  y = 760;
}


    page.drawImage(icon, { x: left, y: y - 2, width: 13, height: 13 });
    catLines.forEach((line, idx) => {
      page.drawText(line, {
        x: left + 19,
        y: y - idx * 18,
        size: 13,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
    });
    y = y - categoryHeight - categoryGap;

    page.drawRectangle({
      x: boxLeft,
      y: y - boxHeight,
      width: boxWidth,
      height: boxHeight,
      borderColor: rgb(0.46, 0.46, 0.46),
      borderWidth: 1,
      borderRadius: 8,
      color: undefined,
    });

    const checkboxField = form.createCheckBox(`${badgeName}_checkbox_${id}`);
    const checkboxX = boxLeft + 13;
    const checkboxY = y - boxPaddingTop - fontSize * 0.25;

    checkboxField.addToPage(page, {
      x: checkboxX,
      y: checkboxY,
      width: 12,
      height: 12,
      borderWidth: 1.2,
      borderColor: rgb(0.5, 0.5, 0.5),
      backgroundColor: rgb(1, 1, 1),
    });
    checkboxField.uncheck();

    // const analysisOffsetX = checkboxX + 18;
    // analysisLines.forEach((line, idx) => {
    //   page.drawText(line, {
    //     x: analysisOffsetX,
    //     y: y - boxPaddingTop - idx * fontSize * 1.35,
    //     font,
    //     size: fontSize,
    //     color: rgb(0.15, 0.17, 0.22),
    //   });
    // });
   const analysisOffsetX = checkboxX + 18;

analysisLines.forEach((line, idx) => {
  if (!line) {
    // blank separator line
    return;
  }

  const trimmed = line.trim();
  const isPageLine = /^-\s*Page\s*\d+/i.test(trimmed);
  const isUrlLine = /^(https?:\/\/|www\.)/i.test(trimmed);

  // indent page+URL lines under the name
  const offsetX =
    isPageLine || isUrlLine ? analysisOffsetX + 12 : analysisOffsetX;

  page.drawText(line, {
    x: offsetX,
    y: y - boxPaddingTop - idx * fontSize * 1.35,
    font,
    size: fontSize,
    color: rgb(0.15, 0.17, 0.22),
  });
});




    const analysisBottomY =
      y - boxPaddingTop - analysisLines.length * fontSize * 1.35 - 16;

    page.drawText('Reason for not addressing:', {
      x: boxLeft + 14,
      y: analysisBottomY - 28,
      font,
      size: 11,
      color: reasonColor,
    });

    const reason = form.createTextField(`${badgeName}_reason_${id}`);
    reason.enableMultiline();
    reason.setText('');
    reason.addToPage(page, {
      x: boxLeft + 13,
      y: analysisBottomY - 58,
      width: boxWidth - 32,
      height: textFieldHeight,
      borderWidth: 1,
      borderColor: rgb(0.7, 0.7, 0.7),
    });
    reason.defaultUpdateAppearances(font, {
      fontSize: 16,
      textColor: rgb(0.1, 0.1, 0.1),
    });
    const reasonField = reason.acroField;
    if (reasonField) {
      reasonField.setFlagTo('Multiline', true);
      reasonField.setFlagTo('RichText', false);
      reasonField.setFlagTo('DoNotScroll', false);
      reasonField.Q = 0;
      reasonField.setDefaultAppearance('/Helv 12 Tf 0 0 0 rg');
    }

    y = y - boxHeight - 34;
    return { page, y };
  }

  // REQUIRED FIXES
  if (requiredFixesCount > 0) {
    page.drawText('Required Fixes', {
      x: left,
      y,
      size: 13,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    drawBadgeWithIcon(page, left + 100, y - 4, iconRed, requiredFixesCount);
    y -= 30;

    for (const fix of analysisData.required_fixes || []) {
      ({ page, y } = renderCardNoRepeatCategory({
        icon: iconRed,
        id: fix.id,
        category: fix.category,
        llm_analysis: fix.llm_analysis,
        badgeName: 'fix',
        reasonColor: rgb(0.44, 0.44, 0.5),
        page,
        y,
      }));
    }

    page.drawLine({
      start: { x: left, y },
      end: { x: left + width, y },
      thickness: 1,
      color: rgb(0.85, 0.85, 0.85),
    });
    y -= 30;
  }

  // PM DISCRETION
  if (pmDiscretionCount > 0) {
    page.drawText('PM Discretion', {
      x: left,
      y,
      size: 13,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    drawBadgeWithIcon(page, left + 92, y - 4, iconYellow, pmDiscretionCount);
    y -= 30;

    for (const pm of analysisData.pm_discretion || []) {
      ({ page, y } = renderCardNoRepeatCategory({
        icon: iconYellow,
        id: pm.id,
        category: pm.category,
        llm_analysis: pm.llm_analysis,
        badgeName: 'pm',
        reasonColor: rgb(0.65, 0.56, 0.4),
        page,
        y,
      }));
    }
  }

  // ========== URL & LINK ANALYSIS: URL001 + URL002 ==========

  const urlRoot = analysisData.url_analysis || {};
  const url001 = urlRoot.URL001_PDF_Link_Extraction || {};
  const url002 = urlRoot.URL002_Embedded_Text_based_URL_Analysis || {};

  if (y < 160) {
    page = pdfDoc.addPage([612, 792]);
    y = 760;
  }

  page.drawText('URL & Link Analysis', {
    x: left,
    y,
    size: 13,
    font: boldFont,
    color: rgb(0, 0, 0),
  });
  y -= 18;

  const descLines2 = splitTextIntoLines(
    'Comprehensive analysis of URLs and links found in the application document.',
    width - 40,
    font,
    11
  );
  descLines2.forEach((line) => {
    page.drawText(line, {
      x: left,
      y,
      size: 11,
      font,
      color: rgb(0.2, 0.2, 0.2),
    });
    y -= 14;
  });
  y -= 8;

  // helper to draw Type/Text/URL table from a pages object
  function drawTypeTextUrlTable(title, pagesObj) {
    const colType = left;
    const colText = left + 70;
    const colUrl = left + 280;

    if (y < 140) {
      page = pdfDoc.addPage([612, 792]);
      y = 760;
    }

    page.drawText(title, {
      x: left,
      y,
      size: 12,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    y -= 16;

    page.drawText('Type', {
      x: colType,
      y,
      size: 12,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    page.drawText('Text', {
      x: colText,
      y,
      size: 10,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    page.drawText('URL', {
      x: colUrl,
      y,
      size: 10,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    y -= 10;

    page.drawLine({
      start: { x: left, y },
      end: { x: left + width, y },
      thickness: 0.7,
      color: rgb(0.8, 0.8, 0.8),
    });
    y -= 10;

    const pageNums = Object.keys(pagesObj || {}).sort(
      (a, b) => Number(a) - Number(b)
    );

    pageNums.forEach((pStr) => {
      const entries = pagesObj[pStr] || [];
      if (!entries.length) return;

      if (y < 60) {
        page = pdfDoc.addPage([612, 792]);
        y = 760;
      }

      page.drawText(`Page ${pStr}`, {
        x: left,
        y,
        size: 10,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      y -= 13;

      // entries.forEach((e) => {
      //   if (y < 40) {
      //     page = pdfDoc.addPage([612, 792]);
      //     y = 760;
      //   }

      //   const typeStr = e.type || 'Text';
      //   const textStr =
      //     e.text && e.text !== '(no text)' ? e.text : '-';
      //   const urlStr = e.url || '-';

      //   page.drawText(typeStr, {
      //     x: colType,
      //     y,
      //     size: 9.5,
      //     font,
      //     color: rgb(0.15, 0.15, 0.15),
      //   });

      //   const textLines = splitTextIntoLines(
      //     textStr,
      //     colUrl - colText - 6,
      //     font,
      //     9
      //   );
      //   const urlLines = splitTextIntoLines(
      //     urlStr,
      //     left + width - colUrl - 6,
      //     font,
      //     9
      //   );
      //   const rowH = Math.max(textLines.length, urlLines.length) * 10;

      //   textLines.forEach((line, i) => {
      //     page.drawText(line, {
      //       x: colText,
      //       y: y - i * 10,
      //       size: 9,
      //       font,
      //       color: rgb(0.15, 0.15, 0.15),
      //     });
      //   });

      //   urlLines.forEach((line, i) => {
      //     page.drawText(line, {
      //       x: colUrl,
      //       y: y - i * 11,
      //       size: 9.5,
      //       font,
      //       color: rgb(0.15, 0.15, 0.15),
      //     });
      //   });

      //   y -= rowH + 4;
      // });
      entries.forEach((e) => {
  if (y < 40) {
    page = pdfDoc.addPage([612, 792]);
    y = 760;
  }

  const typeStr = e.type || 'Text';
  const textStr =
    e.text && e.text !== '(no text)' ? e.text : '-';

  // Combine page number and URL into one string so they stay together
  const pageInfoPrefix = `Page ${pStr}: `;
  const urlOnly = e.url || '-';
  const pageInfo = pageInfoPrefix + urlOnly;

  // Type column (unchanged)
  page.drawText(typeStr, {
    x: colType,
    y,
    size: 9.5,
    font,
    color: rgb(0.15, 0.15, 0.15),
  });

  // Text column
  const textLines = splitTextIntoLines(
    textStr,
    colUrl - colText - 6,
    font,
    9
  );

  // URL column: one block, "Page X: <url>"
  const urlLines = splitTextIntoLines(
    pageInfo,
    left + width - colUrl - 9,
    font,
    9
  );

  const rowH = Math.max(textLines.length, urlLines.length) * 10;

  textLines.forEach((line, i) => {
    page.drawText(line, {
      x: colText,
      y: y - i * 10,
      size: 9,
      font,
      color: rgb(0.15, 0.15, 0.15),
    });
  });

  urlLines.forEach((line, i) => {
    page.drawText(line, {
      x: colUrl,
      y: y - i * 10, // use same step so row height is consistent
      size: 9,
      font,
      color: rgb(0.15, 0.15, 0.15),
    });
  });

  y -= rowH + 4;
});


      y -= 4;
    });
    y -= 8;
  }

  // URL001 table (Type / Text / URL)
  drawTypeTextUrlTable(
    'URL001 - PDF Link Extraction',
    url001.pages || {}
  );

  // URL002 table (Type / Text / URL)
  drawTypeTextUrlTable(
    'URL002 – Embedded/Text-based URL Analysis',
    url002.pages || {}
  );

  try {
    if (form.acroForm && typeof form.acroForm.setNeedAppearances === 'function') {
      form.acroForm.setNeedAppearances(false);
    }
  } catch (e) {}

  const pdfBytes = await pdfDoc.save({
    useObjectStreams: false,
    addDefaultPage: false,
    objectsPerTick: 50,
  });

  return new Blob([pdfBytes], { type: 'application/pdf' });
}
